/**
 * «ЗП» → «Ставка в час»: эффективная ставка по периодам (по умолчанию 3 × 4 недели) —
 * всё начисленное за период (часы, премии, гарантия, доплаты, вычеты) ÷ часы акцепта.
 * Числа — те же, что у `prokat-runner salary-rate`, из `billing/salary_rate.py`.
 */
import { money } from "@/lib/bonus";
import type { RatePeriod, RateRow, SalaryRate } from "@/lib/salary";

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const hours = (minutes: number) => Math.round(minutes / 60);
const cell = "whitespace-nowrap px-3 py-2 text-right align-top";
const first = "sticky left-0 z-10 bg-bg-subtle px-4 py-2 text-left align-top";
const KIND = { base_rate: "ставка часа", guarantee_rate: "гарантия" } as const;

function Period({ p }: { p: RatePeriod }) {
  if (!p.minutes) return <span className="text-fg-faint">нет часов</span>;
  return (
    <>
      <span className="text-[15px] font-semibold tabular-nums">{p.per_hour === null ? "·" : `${money(Math.round(p.per_hour))} ₽`}</span>
      <span className="block text-[11.5px] tabular-nums text-fg-muted" title="начислено за период ÷ часы акцепта">
        {money(Math.round(p.total))} ₽ за {hours(p.minutes)} ч
      </span>
      {p.own_per_hour !== null && p.guarantee > 0 && (
        <span className="block text-[11.5px] tabular-nums text-fg-faint" title="часы и премии без доплаты до гарантии">
          без гарантии {money(Math.round(p.own_per_hour))} ₽ · доплата {p.guarantee_share} %
        </span>
      )}
    </>
  );
}

function Shift({ r }: { r: RateRow }) {
  if (!r.delta) return <span className="text-fg-faint">·</span>;
  const d = r.delta.per_hour;
  return (
    <span className={`text-[14px] font-semibold tabular-nums ${d > 0 ? "text-ok" : d < 0 ? "text-err" : ""}`}>
      {d > 0 ? "+" : d < 0 ? "−" : ""}
      {money(Math.abs(Math.round(d)))} ₽<span className="ml-1 text-[12px] font-normal">({r.delta.per_hour_pct > 0 ? "+" : ""}{r.delta.per_hour_pct} %)</span>
      <span className="block text-[11.5px] font-normal text-fg-muted">за 40 ч {d > 0 ? "+" : d < 0 ? "−" : ""}{money(Math.abs(Math.round(d * 40)))} ₽</span>
    </span>
  );
}

export function RateReport({ r, openUser }: { r: SalaryRate; openUser: ((id: number) => void) | null }) {
  const periods = r.rows[0]?.periods ?? [];
  if (!r.rows.length) return <div className="text-[12.5px] text-fg-muted">За эти недели часов в акцепте нет.</div>;
  return (
    <>
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="ставка в час по периодам">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-muted">
              <tr>
                <th className={`${first} font-medium`}>Сотрудник</th>
                {periods.map((p) => (
                  <th key={p.from} className={`${cell} font-medium`}>
                    <span className="text-fg">
                      {dm(p.from)} — {dm(p.to)}
                    </span>
                    <span className="block font-normal">
                      нед. {p.weeks[0]}–{p.weeks.at(-1)}
                    </span>
                  </th>
                ))}
                <th className={`${cell} font-medium`}>
                  <span className="text-fg">Сдвиг</span>
                  <span className="block font-normal">последний к первому</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((row) => (
                <tr key={row.user.id} className="border-t border-border" data-annot={`ставка ${row.user.name}`}>
                  <th scope="row" className={`${first} font-normal`}>
                    {openUser ? (
                      <button type="button" className="text-left font-medium hover:underline" title="неделя за неделей" onClick={() => openUser(row.user.id)}>
                        {row.user.name}
                      </button>
                    ) : (
                      <span className="font-medium">{row.user.name}</span>
                    )}
                    {row.changes.map((c) => (
                      <span key={`${c.kind}-${c.week}`} className="block text-[11.5px] text-fg-faint">
                        {KIND[c.kind]} {money(c.was)} → {money(c.now)} ₽ с {dm(c.from_date)}
                      </span>
                    ))}
                  </th>
                  {row.periods.map((p) => (
                    <td key={p.from} className={cell}>
                      <Period p={p} />
                    </td>
                  ))}
                  <td className={cell}>
                    <Shift r={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted" data-annot="как считается ставка">
        <h2 className="text-[13px] font-semibold text-fg">Как считается ставка в час</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <b className="font-medium text-fg">Ставка</b> — всё, что начислено за период (часы × ставка часа, премии, доплата до гарантии,
            доплаты вручную, минус вычеты), ÷ часы в акцепте за те же недели. Не среднее недель: неделя с 60 часами весит больше недели с 10.
          </li>
          <li>
            <b className="font-medium text-fg">Без гарантии</b> — та же ставка без доплаты до гарантии: сколько сотрудник заработал часами и премиями.
            Если она ниже гарантии, разницу доплачивает CRM, и ставка упирается в гарантию.
          </li>
          <li>
            Смена ставки часа и гарантии — по проводкам: CRM хранит только текущие значения. Период — {periods[0]?.weeks.length ?? 4}{" "}
            закрытые недели, неполный — первый; идущая неделя не входит.
          </li>
        </ul>
      </section>
    </>
  );
}
